<template>
  <div class="custom-number-input">
    <button
        type="button"
        class="btn-decrement"
        :disabled="isDecrementDisabled"
        @mousedown="onDecrementPress"
        @mouseup="onDecrementRelease"
        @mouseleave="onDecrementRelease"
        @touchstart.prevent="onDecrementPress"
        @touchend.prevent="onDecrementRelease"
    >
      &minus;
    </button>
    <input
        class="number-input"
        :value="internalValue"
        @input="onInput"
        @blur="onBlur"
        type="text"
        inputmode="decimal"
    />
    <button
        type="button"
        class="btn-increment"
        :disabled="isIncrementDisabled"
        @mousedown="onIncrementPress"
        @mouseup="onIncrementRelease"
        @mouseleave="onIncrementRelease"
        @touchstart.prevent="onIncrementPress"
        @touchend.prevent="onIncrementRelease"
    >
      &plus;
    </button>
  </div>
</template>

<script>
export default {
  name: 'CustomNumberInput',
  props: {
    value: { type: Number, default: 0 },
    min: { type: Number, default: -Infinity },
    max: { type: Number, default: Infinity },
    step: { type: Number, default: 1 }
  },
  data() {
    return {
      internalValue: String(this.value),
      incrementTimeout: null,
      incrementInterval: null,
      decrementTimeout: null,
      decrementInterval: null
    };
  },
  computed: {
    isDecrementDisabled() {
      return this.value - this.step < this.min;
    },
    isIncrementDisabled() {
      return this.value + this.step > this.max;
    }
  },
  watch: {
    value(newVal) {
      this.internalValue = String(newVal);
    }
  },
  methods: {
    onInput(event) {
      const val = event.target.value;
      if (/^-?\d*(?:\.\d*)?$/.test(val)) {
        this.internalValue = val;
      }
    },
    onBlur() {
      let num = parseFloat(this.internalValue);
      if (isNaN(num)) {
        num = isFinite(this.min) ? this.min : 0;
      }
      num = Math.min(Math.max(num, this.min), this.max);
      this.$emit('input', num);
      this.internalValue = String(num);
    },
    increment() {
      let num = this.value + this.step;
      if (num > this.max) num = this.max;
      this.$emit('input', num);
    },
    decrement() {
      let num = this.value - this.step;
      if (num < this.min) num = this.min;
      this.$emit('input', num);
    },
    onIncrementPress() {
      this.increment();
      this.clearTimers();
      this.incrementTimeout = setTimeout(() => {
        this.incrementInterval = setInterval(this.increment, 100);
      }, 500);
    },
    onIncrementRelease() {
      this.clearTimers();
    },
    onDecrementPress() {
      this.decrement();
      this.clearTimers();
      this.decrementTimeout = setTimeout(() => {
        this.decrementInterval = setInterval(this.decrement, 100);
      }, 500);
    },
    onDecrementRelease() {
      this.clearTimers();
    },
    clearTimers() {
      if (this.incrementTimeout) {
        clearTimeout(this.incrementTimeout);
        this.incrementTimeout = null;
      }
      if (this.incrementInterval) {
        clearInterval(this.incrementInterval);
        this.incrementInterval = null;
      }
      if (this.decrementTimeout) {
        clearTimeout(this.decrementTimeout);
        this.decrementTimeout = null;
      }
      if (this.decrementInterval) {
        clearInterval(this.decrementInterval);
        this.decrementInterval = null;
      }
    }
  },
  beforeDestroy() {
    this.clearTimers();
  }
};
</script>

<style scoped>
.custom-number-input {
  display: inline-flex;
  align-items: center;
  width: 100%;
}
.number-input {
  width: 50%;
  text-align: center;
  border-radius: 4px;
  font-size: 1rem;
}
.btn-decrement,
.btn-increment {
  background: var(--color-primary-button);
  border: 1px solid var(--color-primary-button-border);
  border-radius: 6px;
  width: 25%;
  height: 50px;
  text-align: center;
  cursor: pointer;
  opacity: 1;
  margin: 5px;
  padding: 0;
}
.btn-decrement:disabled,
.btn-increment:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
